import type { CellCoords } from 'handsontable/base';

import type { Point } from '../../../../common/maths/geometry/pointTypes';
import type { MNHotInstance } from '../tableHotInstanceTypes';

type PendingTouchSelection = {
    coords: CellCoords;
    hasMoved: boolean;
    startPosition: Point;
};

const TOUCH_PAN_THRESHOLD_PX = 20;

/** Returns the first touch from a touch list, including array-like test doubles. */
const getFirstTouch = (touches?: TouchList): Touch | null => touches?.item?.(0) || touches?.[0] || null;

/** Returns the primary touch position for a touch event. */
const getTouchPosition = (event: TouchEvent): Point | null => {
    const touch = getFirstTouch(event.touches) || getFirstTouch(event.changedTouches);
    if (touch) return { x: touch.clientX, y: touch.clientY };

    const mouseEvent = event as unknown as MouseEvent;
    if (!Number.isFinite(mouseEvent.clientX) || !Number.isFinite(mouseEvent.clientY)) return null;

    return { x: mouseEvent.clientX, y: mouseEvent.clientY };
};

/**
 * Defers mobile cell selection until a touch resolves as a tap rather than a pan.
 */
class TouchCellSelectionController {
    private pendingTouchSelection: PendingTouchSelection | null = null;
    private pendingTouchSelectionAbortController: AbortController | null = null;

    constructor(private readonly hotInstance: MNHotInstance) {}

    /**
     * Starts tracking a different-cell touch and prevents Handsontable selecting it immediately.
     */
    begin(event: TouchEvent, coords: CellCoords): boolean {
        const startPosition = getTouchPosition(event);
        if (!startPosition) return false;

        event.stopImmediatePropagation();

        this.pendingTouchSelection = { coords, hasMoved: false, startPosition };
        this.pendingTouchSelectionAbortController = new AbortController();
        const { signal } = this.pendingTouchSelectionAbortController;

        this.hotInstance.rootDocument.addEventListener('touchmove', this.onTouchMove, { passive: true, signal });
        this.hotInstance.rootDocument.addEventListener('touchend', this.onTouchEnd, { capture: true, signal });
        this.hotInstance.rootDocument.addEventListener('touchcancel', this.onTouchCancel, { capture: true, signal });

        return true;
    }

    /**
     * Marks the pending selection as a pan after the touch travels beyond the tap threshold
     */
    private onTouchMove = (event: TouchEvent): void => {
        const pendingTouchSelection = this.pendingTouchSelection;
        const currentPosition = getTouchPosition(event);
        if (!pendingTouchSelection || !currentPosition) return;

        const xDistance = currentPosition.x - pendingTouchSelection.startPosition.x;
        const yDistance = currentPosition.y - pendingTouchSelection.startPosition.y;
        pendingTouchSelection.hasMoved ||= xDistance ** 2 + yDistance ** 2 > TOUCH_PAN_THRESHOLD_PX ** 2;
    };

    /**
     * Applies a deferred selection only when the touch ended as a tap.
     */
    private onTouchEnd = (): void => {
        const pendingTouchSelection = this.pendingTouchSelection;
        this.clearPendingTouchSelection();

        if (!pendingTouchSelection || pendingTouchSelection.hasMoved) return;

        const { row, col } = pendingTouchSelection.coords;
        this.hotInstance.selectCell(row, col, undefined, undefined, false, false);
    };

    /**
     * Clears a deferred selection when the operating system cancels the touch.
     */
    private onTouchCancel = (): void => {
        this.clearPendingTouchSelection();
    };

    /**
     * Releases document-level touch listeners for the active deferred selection.
     */
    private clearPendingTouchSelection(): void {
        this.pendingTouchSelection = null;
        this.pendingTouchSelectionAbortController?.abort();
        this.pendingTouchSelectionAbortController = null;
    }

    /**
     * Clears pending state and document listeners when the table is destroyed.
     */
    dispose(): void {
        this.clearPendingTouchSelection();
    }
}

export default TouchCellSelectionController;
